import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ARCH_STAGES, ARCH_NODES } from "../../data";
import { SectionHeader, Reveal, Label, StatusDot } from "../primitives";

export const Architecture = () => {
  const [active, setActive] = useState("GUARDIAN");
  const node = ARCH_NODES.find((n) => n.id === active);

  return (
    <section className="border-t border-white/[0.06] py-20 md:py-28" data-testid="architecture-section">
      <div className="mx-auto max-w-[1440px] px-5 md:px-8">
        <SectionHeader
          eyebrow="Core Architecture"
          title="THE NEURAL LAYER"
          desc="SYNAPTIX coordinates specialized AI agents around a shared financial intelligence layer. Humans define the mandate — the layer coordinates everything after."
        />

        <div className="grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="relative border border-white/[0.08] bg-panel p-6">
              <div className="absolute bottom-10 left-[39px] top-10 w-px bg-gradient-to-b from-pulse/50 via-brass/40 to-pulse/50" />
              <div className="space-y-1">
                {ARCH_STAGES.map((s, i) => (
                  <div key={s} className="relative flex items-center gap-5 py-3" data-testid={`arch-stage-${i}`}>
                    <span className={`relative z-10 flex h-4 w-4 items-center justify-center border bg-ink-900 ${
                      s === "AI AGENTS" ? "border-pulse" : "border-white/20"}`}>
                      <span className={`h-1.5 w-1.5 ${s === "AI AGENTS" ? "bg-pulse" : "bg-fog-600"}`} />
                    </span>
                    <div className="flex flex-1 items-center justify-between">
                      <span className={`font-mono text-xs tracking-[0.2em] ${s === "AI AGENTS" ? "text-pulse-soft" : "text-fog-100"}`}>{s}</span>
                      <span className="font-mono text-[9px] text-fog-600">0{i + 1}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 border-t border-white/[0.08] pt-4">
                <Label>Data flows downward. Permission flows upward.</Label>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="grid gap-px border border-white/[0.08] bg-white/[0.08] sm:grid-cols-2">
              {ARCH_NODES.map((n) => (
                <button
                  key={n.id}
                  onMouseEnter={() => setActive(n.id)}
                  onFocus={() => setActive(n.id)}
                  onClick={() => setActive(n.id)}
                  data-testid={`arch-node-${n.id.toLowerCase()}`}
                  className={`group relative bg-panel p-5 text-left transition-colors duration-200 ${
                    active === n.id ? "bg-elevated" : "hover:bg-elevated/60"}`}
                >
                  {active === n.id && (
                    <motion.span layoutId="arch-node-edge" className="absolute inset-y-0 left-0 w-[2px] bg-pulse" />
                  )}
                  <div className="flex items-center justify-between">
                    <span className={`font-mono text-sm font-bold tracking-[0.15em] ${active === n.id ? "text-pulse-soft" : "text-fog-100"}`}>
                      {n.id}
                    </span>
                    <StatusDot tone={active === n.id ? "green" : "muted"} />
                  </div>
                  <span className="mt-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-fog-600">Agent Node</span>
                </button>
              ))}

              <div className="relative bg-ink-850 p-5 sm:col-span-2">
                <Label className="mb-2" tone="gold">Node Brief</Label>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={node.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.25 }}
                  >
                    <span className="font-mono text-xs font-bold tracking-[0.2em] text-pulse-soft">{node.id}</span>
                    <p className="mt-2 text-sm leading-relaxed text-fog-400" data-testid="arch-node-desc">{node.desc}</p>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
